import { loadStripe, Stripe } from '@stripe/stripe-js';
import { toast } from '@/lib/utils/toast';
import { supabase } from './supabase/client';

let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = () => {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.warn('Stripe publishable key not found. Payment features will be disabled.');
      return null;
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

/**
 * Redirects to Stripe checkout using a price ID or a lineItems array.
 * Creates a booking and checkout session via the Netlify serverless function.
 *
 * @param experienceId The ID of the experience being booked
 * @param priceId The Stripe Price ID for this product (single-product flow)
 * @param participantCount The number of participants for this booking (single-product flow)
 * @param totalAmount The total amount to charge
 * @param lineItems Array of { price, quantity } for multi-product cart
 * @returns The booking ID if successfully created
 */
export async function redirectToStripeCheckout(
  experienceId: string,
  priceIdOrLineItems: string | { price: string; quantity: number }[],
  participantCount: number,
  totalAmount: number,
  returnUrl?: string // Add returnUrl parameter
) {
  try {
    // Get the current user ID from the auth session
    const { data: authData, error: authError } = await supabase.auth.getSession();
    if (authError || !authData.session?.user) {
      throw new Error('Authentication required');
    }
    const userId = authData.session.user.id;
    console.log('🔍 Using authenticated user ID:', userId);

    // Determine if this is a multi-product (cart) or single-product checkout
    let body: any = {
      experienceId,
      userId,
      totalAmount,
      participantCount,
      returnUrl,
    };
    if (Array.isArray(priceIdOrLineItems)) {
      // Multi-product cart
      body.lineItems = priceIdOrLineItems;
    } else {
      // Single-product
      body.priceId = priceIdOrLineItems;
    }

    // Call our serverless function to create booking & checkout session
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to create checkout session: ${errorData.error}`);
    }

    const { sessionUrl, bookingId } = await response.json();
    console.log('✅ Booking created with ID:', bookingId);
    console.log('🔄 Redirecting to Stripe checkout:', sessionUrl);
    window.location.href = sessionUrl;
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
}

/**
 * Creates a booking record and redirects to Stripe checkout.
 * This function can be used from any experience page and provides
 * error handling and user feedback.
 *
 * @param experienceId The ID of the experience being booked
 * @param priceIdOrLineItems The Stripe Price ID for this product (string) or array of line items
 * @param participantCount The number of participants for this booking (single-product flow)
 * @param totalAmount The total amount to charge
 * @param returnUrl Optional return URL after checkout
 * @returns The booking ID if successfully created
 */
export async function createBookingAndRedirectToStripe(
  experienceId: string,
  priceIdOrLineItems: string | { price: string; quantity: number }[],
  participantCount: number,
  totalAmount: number,
  returnUrl?: string
) {
  console.log('🔍 Starting booking process for experience:', experienceId);
  console.log('🔍 Booking details:', { priceIdOrLineItems, participantCount, totalAmount });
  try {
    await redirectToStripeCheckout(
      experienceId,
      priceIdOrLineItems,
      participantCount,
      totalAmount,
      returnUrl
    );
    toast.success("Booking created - You're being redirected to complete your payment");
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "There was an error creating your booking");
  }
}
